import * as api from '$api/notifications';
import { app } from './app.svelte';
import { featureFlags } from '$lib/config/features';
import { callRingtone } from './call-ringtone';
import { mail } from './mail.svelte';
import { messageSound } from './message-sound';
import { ui } from './ui.svelte';

export type PushPhase = 'unsupported' | 'unavailable' | 'prompt' | 'enabled' | 'denied';

class PushState {
	phase = $state<PushPhase>('unsupported');
	busy = $state(false);
	serverEnabled = $state(false);
	initialized = $state(false);
	bannerDismissed = $state(false);

	isEnabled = $derived(this.phase === 'enabled');
	canEnable = $derived(this.phase === 'prompt');
	showDisabledBanner = $derived(
		this.initialized &&
			!this.bannerDismissed &&
			(this.phase === 'prompt' || this.phase === 'denied')
	);

	private messageListenerInstalled = false;
	private lifecycleListenersInstalled = false;
	private initializing: Promise<void> | null = null;
	private refreshing: Promise<void> | null = null;
	/** Молча восстановить подписку не вышло — до следующего входа больше не пробуем. */
	private restoreFailed = false;

	async init(): Promise<void> {
		this.installShellHook();
		this.installMessageListener();
		this.installLifecycleListeners();
		this.openRequestedChat();

		if (this.initializing) return this.initializing;
		this.initialized = false;
		this.initializing = this.initialize();
		try {
			await this.initializing;
		} finally {
			this.initialized = true;
			this.initializing = null;
		}
	}

	async enable(): Promise<PushPhase> {
		if (!this.serverEnabled || this.busy) return this.phase;
		this.busy = true;
		try {
			const permission = await Notification.requestPermission();
			if (permission !== 'granted') {
				this.transition(permission === 'denied' ? 'denied' : 'prompt');
				return this.phase;
			}

			await this.subscribe();
			this.restoreFailed = false;
			setWanted(true);
			this.transition('enabled');
			return this.phase;
		} finally {
			this.busy = false;
		}
	}

	/** Получить подписку у браузера (или взять уже существующую) и сохранить её на сервере. */
	private async subscribe(): Promise<void> {
		const config = await api.fetchVapidKey();
		if (!config.enabled || !config.publicKey) throw new Error('Web Push не настроен');
		const registration = await serviceWorkerRegistration();
		const existing = await registration.pushManager.getSubscription();
		const subscription =
			existing ??
			(await registration.pushManager.subscribe({
				userVisibleOnly: true,
				applicationServerKey: base64UrlToBytes(config.publicKey)
			}));
		await api.savePushSubscription(serializeSubscription(subscription));
	}

	async disable(): Promise<PushPhase> {
		if (this.busy || !('serviceWorker' in navigator)) return this.phase;
		this.busy = true;
		try {
			// Человек выключил сам — восстанавливать подписку за него больше нельзя.
			setWanted(false);
			const registration = await navigator.serviceWorker.getRegistration();
			const subscription = await registration?.pushManager.getSubscription();
			if (subscription) {
				try {
					await api.deletePushSubscription(subscription.endpoint);
				} finally {
					await subscription.unsubscribe();
				}
			}
			this.transition(Notification.permission === 'denied' ? 'denied' : 'prompt');
			return this.phase;
		} finally {
			this.busy = false;
		}
	}

	/**
	 * Повторно прочитать системное разрешение и локальную подписку.
	 * Используется после возврата из настроек телефона или браузера; сетевой
	 * сбой при таком чтении не должен превращать рабочее состояние в «недоступно».
	 */
	async refresh(): Promise<void> {
		if (!this.initialized || !this.serverEnabled || !supportsPush() || this.busy) return;
		if (this.refreshing) return this.refreshing;

		this.refreshing = this.syncBrowserState();
		try {
			await this.refreshing;
		} catch {
			// Сохраняем последнее достоверное состояние; следующий возврат повторит сверку.
		} finally {
			this.refreshing = null;
		}
	}

	dismissBanner(): void {
		this.bannerDismissed = true;
	}

	reset(): void {
		this.serverEnabled = false;
		this.phase = 'unsupported';
		this.initialized = false;
		this.busy = false;
		this.restoreFailed = false;
	}

	private async initialize(): Promise<void> {
		if (!supportsPush()) {
			this.transition('unsupported');
			return;
		}

		try {
			const config = await api.fetchVapidKey();
			this.serverEnabled = config.enabled && Boolean(config.publicKey);
			if (!this.serverEnabled) {
				this.transition('unavailable');
				return;
			}

			await this.syncBrowserState();
		} catch {
			this.transition('unavailable');
		}
	}

	private async syncBrowserState(): Promise<void> {
		if (Notification.permission === 'denied') {
			this.transition('denied');
			return;
		}

		const registration = await navigator.serviceWorker.getRegistration();
		const existing = await registration?.pushManager.getSubscription();
		if (existing && Notification.permission === 'granted') {
			await api.savePushSubscription(serializeSubscription(existing));
			// Отметка и для тех, кто включил уведомления до её появления.
			setWanted(true);
			this.transition('enabled');
			return;
		}

		/*
		 * Разрешение на месте, человек уведомления здесь включал и сам не выключал, а
		 * подписки нет: её потерял или отобрал браузер. Спрашивать второй раз не о чем —
		 * восстанавливаем молча. Раньше в таком случае приложение снова показывало
		 * «Push-уведомления выключены», и включать приходилось заново — у владельца на
		 * iPhone почти каждый день. Не получилось — остаётся плашка, как и было.
		 */
		if (Notification.permission === 'granted' && isWanted() && !this.restoreFailed) {
			try {
				await this.subscribe();
				this.transition('enabled');
				return;
			} catch {
				this.restoreFailed = true;
			}
		}
		this.transition('prompt');
	}

	private transition(next: PushPhase): void {
		const wasEnabled = this.phase === 'enabled';
		this.phase = next;

		// Успешное включение завершает старый цикл предупреждения. Если после
		// него уведомления снова отключат, баннер должен появиться заново.
		if (next === 'enabled' || (wasEnabled && (next === 'prompt' || next === 'denied'))) {
			this.bannerDismissed = false;
		}
	}

	private installMessageListener(): void {
		if (
			this.messageListenerInstalled ||
			typeof navigator === 'undefined' ||
			!('serviceWorker' in navigator)
		) {
			return;
		}
		this.messageListenerInstalled = true;
		navigator.serviceWorker.addEventListener('message', (event) => {
			if (event.data?.type === 'push-message') {
				const chatId = event.data.chatId;
				const messageId = event.data.messageId;
				if (typeof chatId === 'string' && typeof messageId === 'string') {
					messageSound.emit({ messageId, chatId });
					if (app.connection !== 'open') void this.refreshChat(chatId);
				}
			}
			const callId = event.data?.type === 'push-call' ? event.data.callId : null;
			if (typeof callId === 'string') {
				const timeout = Number(event.data?.ringTimeoutSeconds);
				if (Number.isFinite(timeout)) callRingtone.setDeadline(callId, timeout);
				void this.openCall(callId, false);
			}
			if (event.data?.type === 'open-url' && typeof event.data.url === 'string') {
				// Ответ обязателен: по нему служебный сценарий решает, переходить ли
				// самому — то есть перезагружать ли страницу.
				event.ports[0]?.postMessage({ handled: this.openUrl(event.data.url) });
			}
		});
	}

	/*
	 * Оболочкам (Windows, Android) нажатие на уведомление приходит мимо
	 * служебного сценария. Раньше они просто загружали адрес — страница
	 * стартовала заново, а идущий звонок обрывался. Теперь сначала спрашивают
	 * страницу этой функцией и загружают адрес сами, только если она отказалась.
	 */
	private installShellHook(): void {
		if (typeof window === 'undefined') return;
		window.andresotOpenUrl = (href) => typeof href === 'string' && this.openUrl(href);
	}

	/**
	 * Перейти по адресу из уведомления, не перезагружая приложение.
	 *
	 * `false` — адрес чужой или здесь его открыть нечем: тогда его откроет
	 * обычная навигация. Во время звонка отвечаем «да» на любой свой адрес:
	 * перезагрузка оборвала бы разговор, а окно уже и так показано.
	 */
	private openUrl(href: string): boolean {
		let url: URL;
		try {
			url = new URL(href, location.origin);
		} catch {
			return false;
		}
		// Приложение ещё не запущено (экран входа) или адрес ведёт на другую страницу.
		if (url.origin !== location.origin || location.pathname !== '/' || !app.me.id) return false;
		if (url.pathname !== '/') return Boolean(app.activeCall);

		const callId = url.searchParams.get('call');
		if (callId) {
			void this.openCall(callId, url.searchParams.get('callAction') === 'accept');
			return true;
		}

		const chatId = url.searchParams.get('chat');
		if (chatId) {
			void this.openChat(chatId, url.searchParams.get('message'), true);
			return true;
		}

		// Уведомление о письме ведёт в «Почту».
		if (url.searchParams.get('section') === 'mail' && featureFlags.mail && mail.available) {
			app.setSection('mail');
			ui.showList();
			return true;
		}

		if (url.searchParams.get('section') === 'services') {
			// Раздел сервисов читает адрес сам — по тому же событию, что и «назад».
			history.pushState({}, '', `${url.pathname}${url.search}${url.hash}`);
			window.dispatchEvent(new PopStateEvent('popstate'));
			return true;
		}

		// Просто «открыть приложение» — оно уже открыто.
		if (url.search === '') return true;
		return Boolean(app.activeCall);
	}

	private installLifecycleListeners(): void {
		if (this.lifecycleListenersInstalled || typeof window === 'undefined') return;
		this.lifecycleListenersInstalled = true;

		window.addEventListener('pageshow', () => void this.refresh());
		document.addEventListener('visibilitychange', () => {
			if (document.visibilityState === 'visible') void this.refresh();
		});
	}

	private async refreshChat(chatId: string): Promise<void> {
		try {
			await app.refreshChat(chatId);
		} catch {
			// Reconnect повторит сверку; foreground-push не должен менять текущий экран.
		}
	}

	private openRequestedChat(): void {
		if (typeof location === 'undefined') return;
		const url = new URL(location.href);

		const callId = url.searchParams.get('call');
		if (callId) {
			const connectAccepted = url.searchParams.get('callAction') === 'accept';
			url.searchParams.delete('call');
			url.searchParams.delete('callAction');
			history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
			void this.openCall(callId, connectAccepted);
		}

		const chatId = url.searchParams.get('chat');
		if (!chatId || !app.chats.some((chat) => chat.id === chatId)) return;
		const messageId = url.searchParams.get('message');
		void this.openChat(chatId, messageId);
		url.searchParams.delete('chat');
		url.searchParams.delete('message');
		history.replaceState(history.state, '', `${url.pathname}${url.search}${url.hash}`);
	}

	private async openCall(callId: string, connectAccepted: boolean): Promise<void> {
		try {
			await app.openIncomingCall(callId, connectAccepted);
		} catch {
			// Завершившийся звонок или временно недоступная сеть не должны ломать запуск приложения.
		}
	}

	private async openChat(
		chatId: string,
		messageId?: string | null,
		/** Чат может быть новым: о первом сообщении уведомление приходит раньше списка. */
		mayBeUnknown = false
	): Promise<void> {
		if (!mayBeUnknown && !app.chats.some((chat) => chat.id === chatId)) return;
		try {
			await app.refreshChat(chatId);
		} catch {
			// Чат всё равно открываем с локальной копией; reconnect догонит данные.
		}
		if (!app.chats.some((chat) => chat.id === chatId)) return;
		ui.openChat(chatId);
		if (messageId) app.focusMessage(chatId, messageId);
	}
}

/*
 * «Этот человек включил уведомления в этом браузере и сам их не выключал». Хранится
 * на устройстве и отдельно для каждого аккаунта: чужому входу в том же браузере
 * подписку без спроса не оформляем.
 */
function wantedKey(): string | null {
	return app.me.id ? `andresot:push-wanted:${app.me.id}` : null;
}

function isWanted(): boolean {
	const key = wantedKey();
	if (!key) return false;
	try {
		return localStorage.getItem(key) === '1';
	} catch {
		return false;
	}
}

function setWanted(value: boolean): void {
	const key = wantedKey();
	if (!key) return;
	try {
		if (value) localStorage.setItem(key, '1');
		else localStorage.removeItem(key);
	} catch {
		// Хранилище недоступно (частный режим) — отметки не будет, останется плашка.
	}
}

function supportsPush(): boolean {
	return (
		typeof window !== 'undefined' &&
		/*
		 * В приложении для Windows подписаться нельзя: у Electron нет службы
		 * доставки, и `subscribe` всегда отвечает «push service not available».
		 * Приложение там при каждом запуске предлагало «включить уведомления»,
		 * которые включить невозможно. Уведомления в нём показывает сама оболочка,
		 * пока она запущена (andresotDesktop.notify), — подписка для этого не нужна.
		 */
		!window.andresotDesktop &&
		'serviceWorker' in navigator &&
		typeof PushManager !== 'undefined' &&
		typeof Notification !== 'undefined'
	);
}

async function serviceWorkerRegistration(): Promise<ServiceWorkerRegistration> {
	const timeout = new Promise<never>((_, reject) =>
		setTimeout(() => reject(new Error('Служебный сценарий не запущен')), 5_000)
	);
	return Promise.race([navigator.serviceWorker.ready, timeout]);
}

function base64UrlToBytes(value: string): Uint8Array<ArrayBuffer> {
	const padded = `${value}${'='.repeat((4 - (value.length % 4)) % 4)}`;
	const binary = atob(padded.replace(/-/g, '+').replace(/_/g, '/'));
	const bytes = new Uint8Array(binary.length);
	for (let index = 0; index < binary.length; index += 1) bytes[index] = binary.charCodeAt(index);
	return bytes;
}

function serializeSubscription(subscription: PushSubscription) {
	const json = subscription.toJSON();
	const p256dh = json.keys?.p256dh;
	const auth = json.keys?.auth;
	if (!p256dh || !auth) throw new Error('Браузер не выдал ключи push-подписки');

	return {
		endpoint: subscription.endpoint,
		expirationTime: subscription.expirationTime,
		keys: { p256dh, auth }
	};
}

export const push = new PushState();
